import { Dialog, Transition } from '@headlessui/react';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { ExchangeIcon, PostscriptIcon } from 'components/icons';
import { ButtonCTASmall, SpinnerSmall } from 'components/ui';
import querystring from 'querystring';
import { Fragment, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import {
  useAddIntegrationMutation,
  useGetStoreInfoQuery,
} from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { LocationState } from '../types';

export const PostscriptConnect = () => {
  const { query } = useParseURLQueryParams();
  const history = useHistory();
  const [privateKey, setPrivateKey] = useState('');

  const inputRef = useRef(null);

  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name,
  });

  const [addIntegration, { isLoading }] = useAddIntegrationMutation();

  const location = useLocation<LocationState>();

  const handleCloseModal = () => {
    if (location?.state?.background) {
      history.push(location.state.background);
    } else {
      history.replace({
        ...history.location,
        pathname: '/integrations/connect',
      });
    }
  };

  const handleConnect = () => {
    if (!!!storeInfo?.id || !!!privateKey) return;

    addIntegration({
      storeId: storeInfo?.id,
      provider: 'postscript',
      body: {
        api_key: privateKey.trim(),
      },
    })
      .unwrap()
      .then(handleCloseModal);
  };

  return (
    <Transition show as={Fragment}>
      <Dialog
        as="div"
        id="modal"
        className="fixed inset-0 z-10 overflow-y-auto"
        static
        open
        initialFocus={inputRef}
        onClose={handleCloseModal}
      >
        <div className="min-h-screen text-center md:px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100 "
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl md:h-segments-516px md:w-segments-648px">
              <div className="w-full h-48 px-5 pt-9 lg:px-12 bg-segments-accent-color-1 bg-opacity-5">
                <p className="text-black text-opacity-50 text-segments-13px">
                  Integrations
                </p>
                <div className="flex items-center mt-8 space-x-2 focus:outline-none">
                  <PostscriptIcon />
                  <ExchangeIcon />
                  <p>{storeInfo?.name}</p>
                </div>
                <p className="mt-4 text-2xl">Connect to Postscript</p>
              </div>
              <div className="flex flex-col justify-between px-5 pt-8 text-gray-700 lg:px-12 h-80">
                <div>
                  <p className="text-black text-opacity-50">
                    Looking for your API Private key? Find them here{' '}
                    <span>
                      <a
                        href="https://app.postscript.io/account/api"
                        target="_blank"
                        rel="noreferrer"
                        className="text-segments-link"
                      >
                        Postscript API keys tab
                      </a>
                    </span>
                  </p>
                  <div className="space-y-5">
                    <div className="flex items-center justify-between mt-4">
                      <p className="tex">Private key</p>
                      <input
                        type="text"
                        className="w-2/3 md:w-segments-354px rounded-segments-10px"
                        value={privateKey}
                        onChange={(e) => setPrivateKey(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-6 text-segments-13px p-3 bg-[#FFECD6] rounded-xl w-full">
                  <div className="space-y-3 ">
                    <div className="flex space-x-3">
                      <ExclamationCircleIcon className="w-5 h-5" />

                      <p className="text-black text-opacity-60">
                        Integration with Postscript syncs segment names as
                        "Customer Tags" to existing subscribers. For creating
                        new subscribers, please follow the Postscript
                        "Acquisition" flows.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between mt-8 mb-6 ">
                  <div>
                    <ButtonCTASmall
                      type="button"
                      variant="secondary"
                      onClick={handleCloseModal}
                    >
                      <p className="px-4 font-normal text-segments-13px">
                        Cancel
                      </p>
                    </ButtonCTASmall>
                  </div>
                  <div className="">
                    <ButtonCTASmall
                      variant="main"
                      type="button"
                      onClick={handleConnect}
                      disabled={isLoading || !!!privateKey || !!!storeInfo?.id}
                    >
                      <p className="w-20 text-segments-13px">
                        {isLoading ? <SpinnerSmall /> : 'Submit'}
                      </p>
                    </ButtonCTASmall>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
